'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import type { Session } from '@/payload-types';
import { isError } from '@/utilities/serverError';
import { cn } from '@/utilities/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../_providers/localization/LocalizationProvider';
import { useAuth } from '../_providers/authentication/AuthProvider';
import { deleteSession, getSessions } from './actions';

// ─── Helpers ───────────────────────────────────────────

type ChatTurn = {
  role: string;
  content: string;
};
function getSessionPreview(session: Session, labels: {
  noMessages: string;
  noResponseYet: string;
}): string {
  const chat = session.chat as ChatTurn[] | null;
  if (!chat || !Array.isArray(chat) || chat.length === 0) {
    return labels.noMessages;
  }
  const last = [...chat].reverse().find(m => m.role === 'assistant');
  if (last && typeof last.content === 'string') {
    const text = last.content.replace(/[#*_`>\-\[\]]/g, '').trim();
    return text.length > 50 ? text.slice(0, 50) + '…' : text;
  }
  return labels.noResponseYet;
}
function relativeTime(dateStr: string, labels: {
  now: string;
  minute: string;
  hour: string;
  day: string;
  week: string;
  month: string;
}): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return labels.now;
  if (mins < 60) return `${mins}${labels.minute}`;
  const hrs = Math.floor(diff / 3_600_000);
  if (hrs < 24) return `${hrs}${labels.hour}`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days}${labels.day}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}${labels.week}`;
  return `${Math.floor(days / 30)}${labels.month}`;
}
function dateGroup(dateStr: string, labels: {
  today: string;
  yesterday: string;
  last7Days: string;
  older: string;
}): string {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000);
  if (d >= today) return labels.today;
  if (d >= yesterday) return labels.yesterday;
  if (d >= weekAgo) return labels.last7Days;
  return labels.older;
}

// ─── Component ─────────────────────────────────────────

export type HistoryDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectSession: (session: Session) => void;
  onNewChat: () => void;
  currentSessionId: string | null;
};
export function HistoryDrawer(t0) {
  const $ = _c(89);
  const {
    isOpen,
    onClose,
    onSelectSession,
    onNewChat,
    currentSessionId
  } = t0;
  const {
    user
  } = useAuth();
  const {
    locale,
    messages
  } = useLocalization();
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== currentSessionId || $[1] !== messages.chat.chatDeleted || $[2] !== onNewChat || $[3] !== queryClient) {
    t1 = {
      mutationFn: _temp,
      onSuccess: deletedGuid => {
        queryClient.invalidateQueries({
          queryKey: ["sessions"]
        });
        if (deletedGuid === currentSessionId) {
          onNewChat();
        }
        toast.success(messages.chat.chatDeleted);
      },
      onError: _temp2
    };
    $[0] = currentSessionId;
    $[1] = messages.chat.chatDeleted;
    $[2] = onNewChat;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const deleteMutation = useMutation(t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["sessions"];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !!user;
  let t4;
  if ($[6] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: _temp3,
      enabled: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: t5
  } = useQuery(t4);
  let t6;
  if ($[8] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const sessions = t6;
  let t7;
  bb0: {
    if (!search) {
      t7 = sessions;
      break bb0;
    }
    let t8;
    if ($[10] !== locale || $[11] !== search || $[12] !== sessions) {
      const q = search.toLowerCase();
      t8 = sessions.filter(s => {
        const title = s.title?.toLowerCase() ?? "";
        const preview = getSessionPreview(s, {
          noMessages: locale === "de" ? "Keine Nachrichten" : "No messages",
          noResponseYet: locale === "de" ? "Noch keine Antwort" : "No response yet"
        }).toLowerCase();
        return title.includes(q) || preview.includes(q);
      });
      $[10] = locale;
      $[11] = search;
      $[12] = sessions;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    t7 = t8;
  }
  const filtered = t7;
  let map;
  if ($[14] !== filtered || $[15] !== messages.chat.last7Days || $[16] !== messages.chat.older || $[17] !== messages.chat.today || $[18] !== messages.chat.yesterday) {
    map = new Map();
    for (const s_0 of filtered) {
      const group = dateGroup(s_0.updatedAt, {
        today: messages.chat.today,
        yesterday: messages.chat.yesterday,
        last7Days: messages.chat.last7Days,
        older: messages.chat.older
      });
      if (!map.has(group)) {
        map.set(group, []);
      }
      map.get(group).push(s_0);
    }
    $[14] = filtered;
    $[15] = messages.chat.last7Days;
    $[16] = messages.chat.older;
    $[17] = messages.chat.today;
    $[18] = messages.chat.yesterday;
    $[19] = map;
  } else {
    map = $[19];
  }
  const grouped = map;
  let t8;
  bb1: {
    if (!user) {
      t8 = "?";
      break bb1;
    }
    if (user.username) {
      let t9;
      if ($[20] !== user.username) {
        t9 = user.username.split(/\s+/).map(_temp4).join("").toUpperCase().slice(0, 2);
        $[20] = user.username;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      t8 = t9;
      break bb1;
    }
    let t9;
    if ($[22] !== user.email) {
      t9 = user.email?.slice(0, 2).toUpperCase() ?? "?";
      $[22] = user.email;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t8 = t9;
  }
  const userInitials = t8;
  let t9;
  if ($[24] !== isOpen || $[25] !== onClose) {
    t9 = isOpen && <div className="fixed inset-0 z-40 bg-black/20 md:hidden" onClick={onClose} />;
    $[24] = isOpen;
    $[25] = onClose;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = isOpen ? "w-70 min-w-70 max-md:translate-x-0" : "w-0 min-w-0 max-md:-translate-x-full";
  let t11;
  if ($[27] !== t10) {
    t11 = cn("border-border bg-background shrink-0 overflow-hidden border-r transition-all duration-300 ease-in-out", "flex flex-col", "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:shadow-xl", t10);
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = isOpen ? "opacity-100 delay-100" : "opacity-0";
  let t13;
  if ($[29] !== t12) {
    t13 = cn("flex h-full w-70 min-w-70 flex-col transition-opacity duration-200", t12);
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== messages.chat.history) {
    t14 = <span className="text-foreground text-[13px] font-bold">{messages.chat.history}</span>;
    $[31] = messages.chat.history;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== filtered.length) {
    t15 = <span className="bg-muted text-muted-foreground rounded-full px-1.5 py-px font-mono text-[10px]">{filtered.length}</span>;
    $[33] = filtered.length;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <div className="flex items-center gap-2">{t14}{t15}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <X className="h-4 w-4" />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== onClose) {
    t18 = <button onClick={onClose} className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-7 w-7 items-center justify-center rounded-full transition-colors">{t17}</button>;
    $[39] = onClose;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t16 || $[42] !== t18) {
    t19 = <div className="border-border flex shrink-0 items-center justify-between border-b px-3.5 py-2.5">{t16}{t18}</div>;
    $[41] = t16;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e => setSearch(e.target.value);
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== messages.chat.searchChats || $[47] !== search) {
    t22 = <div className="shrink-0 px-3.5 py-2"><div className="relative">{t20}<input type="text" value={search} onChange={t21} placeholder={messages.chat.searchChats} className="border-border bg-muted text-foreground placeholder:text-muted-foreground focus:border-ring w-full rounded-lg border py-1.75 pr-2.5 pl-8 text-[13px] outline-none" /></div></div>;
    $[46] = messages.chat.searchChats;
    $[47] = search;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== onClose || $[50] !== onNewChat) {
    t23 = () => {
      onNewChat();
      if (window.innerWidth < 768) {
        onClose();
      }
    };
    $[49] = onClose;
    $[50] = onNewChat;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Plus className="h-3.5 w-3.5" />;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== messages.chat.newChat || $[54] !== t23) {
    t25 = <button onClick={t23} className="bg-card border-border text-foreground hover:bg-accent mx-3.5 mb-2 flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors">{t24}{messages.chat.newChat}</button>;
    $[53] = messages.chat.newChat;
    $[54] = t23;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== filtered.length || $[57] !== messages.chat.loginToSeeHistory || $[58] !== messages.chat.noChatsYet || $[59] !== user) {
    t26 = filtered.length === 0 && <p className="text-muted-foreground px-2 py-6 text-center text-xs">{user ? messages.chat.noChatsYet : messages.chat.loginToSeeHistory}</p>;
    $[56] = filtered.length;
    $[57] = messages.chat.loginToSeeHistory;
    $[58] = messages.chat.noChatsYet;
    $[59] = user;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== currentSessionId || $[62] !== deleteMutation || $[63] !== grouped || $[64] !== locale || $[65] !== messages.chat.newChat || $[66] !== messages.chat.now || $[67] !== onClose || $[68] !== onSelectSession) {
    t27 = Array.from(grouped.entries()).map(t28 => {
      const [group_0, items] = t28;
      return <div key={group_0}><div className="text-muted-foreground px-1.5 pt-2 pb-1 text-[11px] font-semibold tracking-wide uppercase">{group_0}</div>{items.map(session => {
          const isActive = session.guid === currentSessionId;
          return <div key={session.id} className="group relative"><button type="button" onClick={() => {
              onSelectSession(session);
              if (window.innerWidth < 768) {
                onClose();
              }
            }} className={cn("relative flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors", isActive ? "bg-muted" : "hover:bg-muted/60")}>{isActive && <span className="bg-primary absolute top-1.5 bottom-1.5 left-0 w-0.75 rounded-r-sm" />}<div className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md", isActive ? "bg-muted-foreground/10" : "bg-muted")}><MessageSquare className="text-muted-foreground h-3.5 w-3.5" /></div><div className="min-w-0 flex-1"><div className="text-foreground truncate text-[13px] leading-tight font-medium">{session.title != "" ? session.title : messages.chat.newChat}</div><div className="text-muted-foreground mt-0.5 truncate text-xs leading-tight">{getSessionPreview(session, {
                    noMessages: locale === "de" ? "Keine Nachrichten" : "No messages",
                    noResponseYet: locale === "de" ? "Noch keine Antwort" : "No response yet"
                  })}</div></div><span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-[10px] group-hover:hidden">{relativeTime(session.updatedAt, {
                  now: messages.chat.now,
                  minute: locale === "de" ? "m" : "m",
                  hour: locale === "de" ? "h" : "h",
                  day: locale === "de" ? "t" : "d",
                  week: locale === "de" ? "w" : "w",
                  month: locale === "de" ? "mo" : "mo"
                })}</span></button><button type="button" onClick={e_0 => {
              e_0.stopPropagation();
              deleteMutation.mutate(session.guid);
            }} className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive absolute top-1/2 right-2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors group-hover:flex"><Trash2 className="h-3.5 w-3.5" /></button></div>;
        })}</div>;
    });
    $[61] = currentSessionId;
    $[62] = deleteMutation;
    $[63] = grouped;
    $[64] = locale;
    $[65] = messages.chat.newChat;
    $[66] = messages.chat.now;
    $[67] = onClose;
    $[68] = onSelectSession;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t26 || $[71] !== t27) {
    t28 = <div className="slim flex-1 overflow-y-auto px-2 py-1">{t26}{t27}</div>;
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== user || $[74] !== userInitials) {
    t29 = user && <div className="border-border flex shrink-0 items-center gap-2 border-t px-3.5 py-2.5"><div className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold">{userInitials}</div><div className="min-w-0 flex-1"><div className="text-foreground truncate text-xs font-semibold">{user.username || user.email}</div><div className="text-muted-foreground text-[11px]">BigScreen</div></div></div>;
    $[73] = user;
    $[74] = userInitials;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t13 || $[77] !== t19 || $[78] !== t22 || $[79] !== t25 || $[80] !== t28 || $[81] !== t29) {
    t30 = <div className={t13}>{t19}{t22}{t25}{t28}{t29}</div>;
    $[76] = t13;
    $[77] = t19;
    $[78] = t22;
    $[79] = t25;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t11 || $[84] !== t30) {
    t31 = <aside className={t11}>{t30}</aside>;
    $[83] = t11;
    $[84] = t30;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== t31 || $[87] !== t9) {
    t32 = <>{t9}{t31}</>;
    $[86] = t31;
    $[87] = t9;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  return t32;
}
function _temp4(w) {
  return w[0];
}
async function _temp3() {
  const result_0 = await getSessions();
  if (isError(result_0)) {
    toast.error(result_0.message);
    return [];
  }
  return result_0;
}
function _temp2(error) {
  toast.error(error.message);
}
async function _temp(guid) {
  const result = await deleteSession(guid);
  if (isError(result)) {
    throw new Error(result.message);
  }
  return guid;
}